import { CornerUpLeft } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";

import { toConvexTableId } from "~/lib/convex-table-id";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form";
import { MultiCombobox } from "../../../ui/multi-combobox";
import { Switch } from "../../../ui/switch";
import type { ItemFormData, ModifierGroupForForm } from "./item-form-types";

interface ModifiersSectionProps {
  form: UseFormReturn<ItemFormData, unknown, ItemFormData>;
  modifierGroups: ModifierGroupForForm[];
}

export const ModifiersSection = ({
  form,
  modifierGroups,
}: ModifiersSectionProps) => (
  <div className="mt-10 space-y-5">
    <div className="space-y-1">
      <h3 className="subtitle-sm text-foreground">Modifiers</h3>
      <p className="body-sm text-muted-foreground">
        Customize item options such as extras, sizes and special requests
      </p>
    </div>

    <FormField
      control={form.control}
      name="useCategoryModifiers"
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center space-x-2">
            <FormLabel>Use category modifiers</FormLabel>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />

    <div className="space-y-2">
      <p className="label text-foreground">Apply modifiers to this item only</p>
      <FormField
        control={form.control}
        name="modifierGroups"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <MultiCombobox
                groups={["default"]}
                items={{
                  default: modifierGroups?.map((modifierGroup) => ({
                    value: modifierGroup._id,
                    label: modifierGroup.name,
                    component: (
                      <div className="space-y-2">
                        <div>
                          <div className="flex items-center gap-1">
                            <p className="text-foreground">
                              {modifierGroup.name}
                            </p>
                            <span className="caption bg-muted text-muted-foreground grid size-5 place-items-center rounded-full">
                              {modifierGroup.modifiers.length +
                                modifierGroup.subModifierGroups.length}
                            </span>
                          </div>
                          <p className="body-sm text-muted-foreground">
                            {[
                              ...modifierGroup.modifiers.map(
                                (m) =>
                                  `${m.name}${
                                    m.additionalCost
                                      ? ` (${(typeof m.additionalCost ===
                                        "string"
                                          ? Number.parseFloat(m.additionalCost)
                                          : m.additionalCost
                                        ).toLocaleString("en-US", {
                                          style: "currency",
                                          currency: "USD",
                                        })})`
                                      : ""
                                  }`
                              ),
                              ...modifierGroup.subModifierGroups.map(
                                (m) => `${m.name} (${m.modifiers.length})`
                              ),
                            ].join(", ")}
                          </p>
                        </div>
                        {modifierGroup.subModifierGroups.map((m) => (
                          <div className="flex items-start gap-2" key={m.id}>
                            <CornerUpLeft className="text-success size-5" />
                            <div>
                              <div className="flex items-center gap-1">
                                <p className="label text-foreground">
                                  {m.name}
                                </p>
                                <span className="caption bg-muted text-muted-foreground grid size-5 place-items-center rounded-full">
                                  {m.modifiers.length}
                                </span>
                              </div>
                              <p className="body-sm text-muted-foreground">
                                {m.modifiers
                                  .map(
                                    (modifier) =>
                                      `${modifier.name}${
                                        modifier.additionalCost
                                          ? ` (${(typeof modifier.additionalCost ===
                                            "string"
                                              ? Number.parseFloat(
                                                  modifier.additionalCost
                                                )
                                              : modifier.additionalCost
                                            ).toLocaleString("en-US", {
                                              style: "currency",
                                              currency: "USD",
                                            })})`
                                          : ""
                                      }`
                                  )
                                  .join(", ")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                  })),
                }}
                onValueChange={(d) =>
                  field.onChange(
                    d.default.map((id) => toConvexTableId("categories", id))
                  )
                }
                values={{
                  default: field.value.map((id) => String(id)),
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  </div>
);
